import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { collection, addDoc } from "firebase/firestore";
import { db, trackLead, trackFormStep } from "../firebase/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Sun } from "lucide-react";
import "./styles/Quote.css";
import ProgressBar from "../components/ProgressBar";
import Step1Name from "../components/Step1Name";
import Step2Contact from "../components/Step2Contact";
import Step3Address from "../components/Step3Address";
import Step4Property from "../components/Step4Property";
import Step5Roof from "../components/Step5Roof";
import Step6Billing from "../components/Step6Billing";
import Step7Consent from "../components/Step7Consent";
import Step8Review from "../components/Step8Review";
import FormAside from "../components/FormAside";
import { useRecaptcha } from "../hooks/useRecaptcha";
import { FirebaseError } from "firebase/app";
import { useNavigate, useLocation } from 'react-router-dom';

/* Prefill support: the savings calculator hands its inputs over via
   navigate("/quote", { state: { fromCalculator, usState, monthlyBill } }) */
const STATE_ABBR: Record<string, string> = {
  Alabama: "AL", Arizona: "AZ", California: "CA", Colorado: "CO",
  Florida: "FL", Georgia: "GA", Idaho: "ID", Illinois: "IL",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Nevada: "NV",
  "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", Ohio: "OH", Oregon: "OR", Pennsylvania: "PA",
  "South Carolina": "SC", Texas: "TX", Utah: "UT", Virginia: "VA",
  Washington: "WA", Wisconsin: "WI",
};

function billToRange(bill: number): string | undefined {
  if (!bill || bill <= 0) return undefined;
  if (bill <= 100) return "0-100";
  if (bill <= 200) return "100-200";
  if (bill <= 300) return "200-300";
  return "300+";
}

const formSchema = z.object({
  firstName: z.string().min(2).max(50),
  lastName: z.string().min(2).max(50),
  email: z.string().email().max(100),
  phone: z
    .string()
    .min(10)
    .max(15)
    .transform((val) => val.replace(/\D/g, "")),
  address: z.string().min(5).max(100),
  city: z.string().min(2).max(50),
  state: z.string().length(2),
  zipCode: z.string().min(5).max(10),
  propertyType: z.enum(["residential", "commercial", "industrial"]),
  propertyOwnership: z.enum(["owned", "rented"]),
  roofType: z.enum(["asphalt", "metal", "tile", "flat"]),
  roofAge: z.enum(["0-5", "5-10", "10-15", "15+"]),
  averageMonthlyBill: z.enum(["0-100", "100-200", "200-300", "300+"]),
  additionalInfo: z.string().max(500).optional(),
  // Must be explicitly true — TCPA requires consent before we can call/text or sell this lead.
  contactConsent: z.boolean().refine((v) => v === true, {
    message: "You must consent to be contacted to continue",
  }),
  companyName: z.string(), // honeypot
});

type FormData = z.infer<typeof formSchema>;

interface RecaptchaVerificationRequest {
  token: string;
  action: string;
  userInfo?: {
    accountId: string;
    email?: string;
    phoneNumber?: string;
    username?: string;
  };
}

interface RecaptchaVerificationResponse {
  success: boolean;
  score: number;
  action: string;
  reasons: string[];
  accountDefenderLabels: string[];
  assessmentName: string;
}

const functions = getFunctions();
const verifyRecaptcha = httpsCallable<
  RecaptchaVerificationRequest,
  RecaptchaVerificationResponse
>(functions, "verifyRecaptcha");

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export default function Quote() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isReady, execute, reset } = useRecaptcha(RECAPTCHA_SITE_KEY);
  const navigate = useNavigate();
  const location = useLocation();
  const calcPrefill = (location.state as {
    fromCalculator?: boolean;
    usState?: string;
    monthlyBill?: string;
  } | null);
  const prefilledState = calcPrefill?.fromCalculator && calcPrefill.usState
    ? STATE_ABBR[calcPrefill.usState] ?? ""
    : "";
  const prefilledBill = calcPrefill?.fromCalculator && calcPrefill.monthlyBill
    ? billToRange(parseFloat(calcPrefill.monthlyBill))
    : undefined;

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: prefilledState,
      zipCode: "",
      propertyType: undefined,        // User must select (residential/commercial/etc.)
      propertyOwnership: undefined,  // User must select (owned/rented/etc.)
      roofType: undefined,           // User must select (asphalt/tile/etc.)
      roofAge: undefined,            // User must select (e.g., 0-5/6-10/etc.)
      averageMonthlyBill: (prefilledBill as FormData["averageMonthlyBill"]) ?? undefined,
      additionalInfo: "",
      contactConsent: false,         // Default to false – user must opt-in
      companyName: "",               // Honeypot stays empty
    },
  });
  

  const totalSteps = 8;

  const nextStep = async () => {
    const stepFields: Record<number, (keyof FormData)[]> = {
      1: ["firstName", "lastName"],
      2: ["email", "phone"],
      3: ["address", "city", "state", "zipCode"],
      4: ["propertyType", "propertyOwnership"],
      5: ["roofType", "roofAge"],
      6: ["averageMonthlyBill"],
      7: ["contactConsent"],
    };

    const fieldsToValidate = stepFields[currentStep] || [];
    if (fieldsToValidate.length > 0) {
      const isValid = await methods.trigger(fieldsToValidate);
      if (!isValid) return;
    }

    trackFormStep("solar-quote", currentStep + 1);
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    try {
      if (data.companyName !== "") return;
      if (!isReady) throw new Error("reCAPTCHA not ready");
      const token = await execute("submit");
      if (!token) throw new Error("reCAPTCHA token missing");

      const result = await verifyRecaptcha({
        token,
        action: "submit",
        userInfo: {
          accountId: data.email,
          email: data.email,
          phoneNumber: data.phone,
          username: `${data.firstName} ${data.lastName}`,
        },
      });

      if (!result.data.success) {
        throw new Error("reCAPTCHA failed or low score");
      }

      await addDoc(collection(db, "quotes"), {
        ...data,
        type: "solar", // tags the lead so mirrorSolarLead copies it into the LeadSRC marketplace
        createdAt: new Date().toISOString(),
        recaptchaScore: result.data.score,
        recaptchaAction: result.data.action,
        recaptchaAssessmentName: result.data.assessmentName ?? null,
      });

      localStorage.setItem("solarCalculatorUnlocked", "true");
      localStorage.setItem(
        "solarCalculatorData",
        JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          contactConsent: data.contactConsent,
        })
      );

      trackLead("solar-quote", { value: 45, currency: "USD" });
      setIsSubmitted(true);
    } catch (err) {
      const firebaseError = err as FirebaseError;
      setError(firebaseError.message);
      reset();
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="solar-form-card">
        <div className="solar-form-card-header text-center">
          <h2 className="solar-form-card-title success">Thank You!</h2>
          <p className="solar-form-card-description">
            Your solar panel inquiry has been submitted successfully. A
            consultant will contact you shortly.
          </p>
        </div>
        <div className="solar-form-card-content center">
          <Sun className="solar-form-icon" />
        </div>
        <div className="solar-form-card-footer center">
          <button
            onClick={() => setIsSubmitted(false)}
            className="solar-form-reset-btn"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="form-layout">
        <div className="solar-form-card">
          <div className="solar-form-card-header">
            <h2 className="solar-form-card-title">Solar Panel Inquiry Form</h2>
            <p className="solar-form-card-description">
              Fill out your details to receive a customized solar estimate.
            </p>
          </div>
          <div className="solar-form-card-content">
            <FormProvider {...methods}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  methods.handleSubmit(onSubmit)(e);
                }}
                className="solar-form"
              >
                <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
                <p className="solar-step-count">
                  Step {currentStep} of {totalSteps}
                </p>
                <div className="solar-form-section">
                  {currentStep === 1 && <Step1Name />}
                  {currentStep === 2 && <Step2Contact />}
                  {currentStep === 3 && <Step3Address />}
                  {currentStep === 4 && <Step4Property />}
                  {currentStep === 5 && <Step5Roof />}
                  {currentStep === 6 && <Step6Billing />}
                  {currentStep === 7 && <Step7Consent />}
                  {currentStep === 8 && <Step8Review />}
                </div>
                <div className="solar-form-navigation">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="solar-form-prev-btn"
                    >
                      Previous
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="solar-form-next-btn"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="solar-form-submit"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit Inquiry"}
                    </button>
                  )}
                </div>
                {error && <div className="solar-form-error">{error}</div>}
                <div className="recaptcha-notice">
                  <small>Protected by reCAPTCHA Enterprise</small>
                  <p>
                    This site is protected by reCAPTCHA and the Google{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </p>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
        <FormAside
          heading="Why homeowners trust us"
          points={[
            "100% free — there's never a cost to you",
            "No spam, no robocalls, ever",
            "Takes about 2 minutes",
          ]}
          steps={[
            {
              title: "Tell us about your home",
              text: "A few quick questions about your roof and energy bill.",
            },
            {
              title: "We match you with vetted pros",
              text: "Trusted local installers — no pressure to buy.",
            },
            {
              title: "Compare quotes & save",
              text: "Review your options side by side and choose.",
            },
          ]}
          footnote="Your details are only shared with the installers you choose to hear from."
        />
        </div>
      </div>
    </div>
  );
}
